import messages from "./input-messages.json";

type RequestInputCode = keyof typeof messages;

export class RequestInputValidationError extends Error {
  constructor(readonly code: RequestInputCode) {
    super(code);
    this.name = "RequestInputValidationError";
  }
}

export function requestInputMessage(code: RequestInputCode, language: string) {
  return messages[code][language.startsWith("ar") ? "ar" : "en"];
}

function checkText(value: string) {
  if (
    Array.from(value).some((character) => {
      const point = character.codePointAt(0)!;
      return point === 0 || (point >= 0xd800 && point <= 0xdfff);
    })
  )
    throw new RequestInputValidationError("text");
}

/** Validate before JSON.stringify can turn an invalid number into null. */
export function serializeJsonBody(body: unknown): string {
  try {
    const encoded = JSON.stringify(
      body,
      function (this: unknown, key: string, value: unknown) {
        checkText(key);
        const original =
          this && typeof this === "object"
            ? (this as Record<string, unknown>)[key]
            : undefined;
        if (original instanceof Date && !Number.isFinite(original.getTime()))
          throw new RequestInputValidationError("date");
        if (
          (typeof value === "number" && !Number.isFinite(value)) ||
          (original instanceof Number && !Number.isFinite(original.valueOf()))
        )
          throw new RequestInputValidationError("number");
        if (typeof value === "string") checkText(value);
        if (original instanceof String) checkText(original.valueOf());
        if (
          typeof value === "bigint" ||
          typeof value === "function" ||
          typeof value === "symbol" ||
          (value === undefined &&
            (original !== undefined || Array.isArray(this)))
        )
          throw new RequestInputValidationError("structure");
        if (
          value instanceof Map ||
          value instanceof Set ||
          value instanceof WeakMap ||
          value instanceof WeakSet ||
          value instanceof RegExp ||
          value instanceof Error ||
          value instanceof Promise ||
          value instanceof Blob ||
          value instanceof FormData ||
          value instanceof URLSearchParams ||
          value instanceof ArrayBuffer ||
          ArrayBuffer.isView(value)
        )
          throw new RequestInputValidationError("structure");
        return value;
      },
    );
    if (encoded === undefined)
      throw new RequestInputValidationError("structure");
    return encoded;
  } catch (error) {
    if (error instanceof RequestInputValidationError) throw error;
    // Cycles, unsupported values and throwing toJSON methods receive a safe
    // refusal; native exception text may contain submitted data.
    throw new RequestInputValidationError("structure");
  }
}
